import BaseFormGroup from 'components/atoms/base-form/group';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import BaseModal from 'components/atoms/base-modal';
import BaseSpinWrapper from 'components/atoms/base-spin-wrapper';
import { CLINIC_CARE_MANAGERS_TABLE_ITEMS_PER_PAGE } from 'constants/clinicCareManagers';
import { useClinicCareManagersSelectBox } from 'infrastructure/hooks/clinic/use-clinic-care-managers-select-box';
import { useQueryClient } from '@tanstack/react-query';
import { getClinicCareManagersGetApiQueryKey } from 'infrastructure/hooks/clinic/use-clinic-care-managers';
import { useFormik } from 'formik';
import { isEqual } from 'lodash';
import useUser from 'utils/useUser';

import CareManagersTable from '../../care-manager-table';
import s from './styles.module.scss';
import { CareManagersDropdown } from './dropdown';
import { clinicCareManagersValidationSchema } from './validation-schema';

import type { IBaseSelectBoxOption } from 'components/atoms/base-select-box/types';
import type { PreparedSelectCareManagersItems } from 'infrastructure/hooks/clinic/use-clinic-care-managers-select-box';
import type {
  IClinicCareManager,
  IClinicCareManagerLite,
  IClinicCreateCareManager,
} from 'infrastructure/interfaces';
import type { Clinic } from 'infrastructure/classes/clinic';
import type {
  IIndependentFormProps,
  IIndependentFormRef,
} from 'infrastructure/interfaces/common/i-independent-form';

const initialValues: IClinicCreateCareManager = {
  careManagers: [],
};

interface IProps extends IIndependentFormProps {
  data?: Clinic;
  isEditMode?: boolean;
  agencyGuid?: string | null;
}

interface IControlsProps {
  onClick: () => void;
  disabled: boolean;
}

interface IResponse {
  count: number;
  items: IClinicCareManager[];
}
const FormDisplayName = 'ClinicCareManagersForm';
const Controls: React.FC<IControlsProps> = (props) => {
  const { onClick, disabled } = props;
  const { t } = useTranslation();
  return (
    <BaseButton
      disabled={disabled}
      dataCy="editClinicCareManagers-button"
      width="160px"
      onClick={onClick}
      label={t('controls.edit')}
    />
  );
};

const ClinicCareManagersForm = forwardRef<
  IIndependentFormRef<IClinicCreateCareManager>,
  IProps
>((props, ref) => {
  const { data, isEditMode, agencyGuid } = props;
  const { t } = useTranslation();
  const [open, setOpen] = useState<boolean>(false);
  const user = useUser();
  const [addDoctorsGuids, setAddDoctorsGuids] = useState<string[]>([]);
  const [deleteDoctorsGuids, setDeleteDoctorsGuids] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { mutate, careManagers } = useClinicCareManagersSelectBox({
    clinicGuid: data?.guid,
    page: 1,
    items: 1000,
  });

  const [careManagersView, setCareManagersView] = useState<
    PreparedSelectCareManagersItems[]
  >([]);
  const [tableCareManagers, setTableCareManagers] = useState<
    IClinicCareManagerLite[]
  >([]);

  const queryClient = useQueryClient();
  const [page, setPage] = useState<number>(1);
  const [count, setCount] = useState<number>(0);

  useEffect(() => {
    if (!isEqual(careManagersView, careManagers)) {
      setCareManagersView(careManagers);
    }
  }, [careManagers]);

  const careManagersQueryKey = getClinicCareManagersGetApiQueryKey(
    page,
    CLINIC_CARE_MANAGERS_TABLE_ITEMS_PER_PAGE,
    data?.guid,
  );

  const submitForm = async () => {
    if (isEditMode) {
      setLoading(true);
      if (user.isAgencyDoctor) {
        mutate(
          {
            addDoctorsGuids,
            deleteDoctorsGuids,
          },
          {
            onSettled() {
              setLoading(false);
            },
          },
        );
      }
      return {
        values: formik.values,
        formName: FormDisplayName,
        isValid: true,
      };
    }
    const errors = await formik.validateForm();
    const isValid = Object.keys(errors).length === 0;
    return {
      values: formik.values,
      formName: FormDisplayName,
      isValid,
    };
  };
  const currentGuids = careManagers?.map(({ value }) => value) || [];
  const formik = useFormik<IClinicCreateCareManager>({
    initialValues,
    onSubmit: submitForm,
    validateOnChange: false,
    validationSchema: open ? clinicCareManagersValidationSchema() : undefined,
  });

  const { values, setFieldValue } = formik;

  useImperativeHandle(ref, () => ({
    submit: submitForm,
    reset: formik.resetForm,
  }));

  const handleUpdate = (newAdd: string[], newDel: string[]) => {
    setAddDoctorsGuids(newAdd);
    setDeleteDoctorsGuids(newDel);
  };

  const handleSelect = (
    val: string,
    option: IBaseSelectBoxOption<PreparedSelectCareManagersItems, any>,
  ) => {
    if (!currentGuids.includes(val)) {
      const newAdd = [...new Set([...addDoctorsGuids, val])];
      handleUpdate(
        newAdd,
        deleteDoctorsGuids.filter((id) => id !== val),
      );

      setFieldValue('careManagers', [...(values.careManagers ?? []), val]);
    }
    setCareManagersView([
      ...careManagersView,
      option as unknown as PreparedSelectCareManagersItems,
    ]);
  };

  const handleDeselect = (val: string) => {
    if (currentGuids.includes(val)) {
      const newDel = [...new Set([...deleteDoctorsGuids, val])];
      handleUpdate(
        addDoctorsGuids.filter((id) => id !== val),
        newDel,
      );
    } else {
      const newAdd = addDoctorsGuids.filter((id) => id !== val);
      handleUpdate(newAdd, deleteDoctorsGuids);
      setFieldValue(
        'careManagers',
        [...(values.careManagers ?? [])].filter((guid) => guid !== val),
      );
    }
    setCareManagersView([...careManagersView].filter((cm) => cm.value !== val));
  };

  const onEdit = () => setOpen(true);
  const onCancel = () => setOpen(false);
  const onOk = () => {
    setOpen(false);
    setTableCareManagers(() => {
      return careManagersView.map((cm) => ({
        firstName: cm.label.split(' ')[0],
        lastName: cm.label.split(' ')[1],
        doctorGuid: cm.value,
      }));
    });
  };

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      const updatedData =
        queryClient.getQueryData<IResponse>(careManagersQueryKey);
      if (updatedData?.count !== count) {
        setCount(updatedData?.count ?? 0);
      }
    });

    return () => unsubscribe();
  }, [queryClient, careManagersQueryKey, count]);

  return (
    <BaseFormGroup
      controls={
        isEditMode ? (
          <Controls disabled={!user.isAgencyDoctor} onClick={onEdit} />
        ) : undefined
      }
      label={
        isEditMode
          ? t('labels.ccmSpecialists')
          : `${t('labels.ccmSpecialists')}: ${count}`
      }
    >
      {isEditMode ? (
        <>
          <CareManagersTable
            isEditable
            customItems={tableCareManagers}
            setPage={setPage}
            page={page}
            items={CLINIC_CARE_MANAGERS_TABLE_ITEMS_PER_PAGE}
            clinicGuid={data?.guid}
          />
          <BaseModal
            title={`${t('labels.select')} ${t('labels.ccmSpecialists')}`}
            open={open}
            onCancel={onCancel}
            width="1000px"
          >
            <BaseSpinWrapper spinning={loading}>
              <CareManagersDropdown
                disabled={loading}
                defaultValue={careManagersView || []}
                onSelect={handleSelect}
                onDeselect={handleDeselect}
                agencyGuid={agencyGuid || ''}
              />
              <div className={s['care-managers-submit-wrapper']}>
                <BaseButton
                  disabled={loading}
                  dataCy="save-care-managers"
                  label={t('controls.save')}
                  onClick={onOk}
                />
              </div>
            </BaseSpinWrapper>
          </BaseModal>
        </>
      ) : (
        <CareManagersDropdown
          disabled={loading}
          defaultValue={careManagersView || []}
          onSelect={handleSelect}
          onDeselect={handleDeselect}
          agencyGuid={agencyGuid || ''}
        />
      )}
    </BaseFormGroup>
  );
});

export default ClinicCareManagersForm;
